import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from "react";
import axios from "axios";

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: "info" | "success" | "warning" | "error";
  link?: string | null;
  is_read: boolean;
  createdAt: string;
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  fetchNotifications: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  removeNotification: (id: string) => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

// Axios instance or you can use your existing interceptor-based api client
// Assuming base URL configuration and Authorization header are handled elsewhere,
// we'll use a relative path /api/notifications for now. Let's rely on standard fetch or axio if there's an API config.
// I'll assume an api instance exists or can be imported. Let's use axios directly with Authorization header from localStorage just in case, or rely on a standard API utility if present.
// For now, I'll use standard axios and assume token is in localStorage.

const getAuthHeaders = () => {
  const token = localStorage.getItem("token"); // adjust if your token key is different
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const fetchNotifications = useCallback(async () => {
    try {
      // Fetching all notifications (limit 100 on backend)
      const res = await axios.get(`${BASE_URL}/api/notifications`, {
        headers: getAuthHeaders(),
      });
      if (res.data.success) {
        setNotifications(res.data.data);
      }
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    }
  }, []);

  useEffect(() => {
    // 1. Fetch on initial mount
    fetchNotifications();

    // 2. Fetch on focus (sync with backend when user returns to tab)
    const onFocus = () => {
      fetchNotifications();
    };
    window.addEventListener("focus", onFocus);

    // 4. Custom event listener for local dispatch
    const handleLocalDispatch = (e: Event) => {
      const customEvent = e as CustomEvent<Notification>;
      if (customEvent.detail) {
        setNotifications((prev) => [customEvent.detail, ...prev]);
      }
    };
    window.addEventListener("app:add-notification", handleLocalDispatch);

    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("app:add-notification", handleLocalDispatch);
    };
  }, [fetchNotifications]);

  const markAsRead = async (id: string) => {
    // Optimistic Update
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
    );
    try {
      await axios.put(`${BASE_URL}/api/notifications/${id}/read`, {}, {
        headers: getAuthHeaders(),
      });
    } catch (error) {
      console.error("Failed to mark as read:", error);
      // Revert optimistic update
      fetchNotifications();
    }
  };

  const markAllAsRead = async () => {
    // Optimistic Update
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    try {
      await axios.put(`${BASE_URL}/api/notifications/mark-all-read`, {}, {
        headers: getAuthHeaders(),
      });
    } catch (error) {
      console.error("Failed to mark all as read:", error);
      fetchNotifications();
    }
  };

  const removeNotification = async (id: string) => {
    // Optimistic Update
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    try {
      await axios.delete(`${BASE_URL}/api/notifications/${id}`, {
        headers: getAuthHeaders(),
      });
    } catch (error) {
      console.error("Failed to delete notification:", error);
      fetchNotifications();
    }
  };

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        fetchNotifications,
        markAsRead,
        markAllAsRead,
        removeNotification,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotificationContext = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error("useNotificationContext must be used within a NotificationProvider");
  }
  return context;
};

// Global helper to trigger optimistic notification add from anywhere
export const triggerAppNotification = (detail: Notification) => {
  window.dispatchEvent(new CustomEvent("app:add-notification", { detail }));
};
